import { useCallback, useEffect, useState } from 'react';
import type React from 'react';

import { Button } from '@change/ds-core-ui';
import { SignaturesIcon } from '@change/ds-icons';

import { Translate } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';

import { usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import type { PetitionDetailsShareCtaPlacementExperimentVariation } from '#src/app/pages/petitionDetails/pageContext';
import { useMobileNativeShare, useSignatureTracking } from '#src/app/pages/petitionDetails/shared/hooks';
import { TrackExperimentExposure } from '#src/app/shared/components/tracking';

import { ActionButtonWrapper } from './ActionButtonWrapper';
import type { ActionPanelVariant } from './MobileActionButton';
import { MobileSignModal, usePreloadMobileSignModal } from './MobileSignModal';
import { useMobileModalContext } from './shared/useMobileModalContext';
import { useShowSignForm } from './shared/useShowSignForm';
import { SignCtaShareAffordance } from './SignCtaShareAffordance';

type Props = {
	sticky: boolean;
	visible: boolean;
	variant: ActionPanelVariant;
};

export const MobileSignButtonContainer: React.FC<Props> = ({ sticky, visible, variant }) => {
	const [isOpen, setIsOpen] = useState(false);
	const { trackMobileSignIntent } = useSignatureTracking();

	const context = useMobileModalContext();

	usePreloadMobileSignModal();

	const {
		data: {
			experiments: {
				// Named `sharePlacementVariation`, never `variation`/`variant`: this file already has an
				// unrelated `variant: ActionPanelVariant` prop with the same `'v1' | 'v2'` values (threaded
				// into MobileSignModal below) — a bare `variation` local would be a live miswiring hazard.
				petitionDetailsShareCtaPlacementExperimentVariation: sharePlacementVariation,
				petitionDetailsShareCtaPlacementExperiment,
			},
		},
	} = usePetitionDetailsPageUserData();

	// Render site, derived from the existing `sticky` prop — both instances render the affordance,
	// so this has no `sticky` gate; `sticky` only decides the tracking context value.
	const trackingContext = sticky ? 'petition_show_sign_cta_sticky' : 'petition_show_sign_cta_inline';

	// Evaluated in every arm, control included: this is the availability half of the render/exposure
	// predicate, and it must be arm-independent so the compared populations are identical.
	const shareAction = useMobileNativeShare(trackingContext);

	const placement = getSharePlacement(sharePlacementVariation);

	const openModal = useCallback(() => {
		trackMobileSignIntent();
		setIsOpen(true);
	}, [trackMobileSignIntent]);

	const openModalWithoutTracking = useCallback(() => {
		setIsOpen(true);
	}, []);

	const signButton = (
		<Button
			variant="primary"
			onClick={openModal}
			leadingIcon={<SignaturesIcon size="small" />}
			data-qa="mobile-sign-button"
			data-testid="mobile-sign-button"
			data-gtm="ga4-sign-button"
			size="large"
			className={placement === 'beside' ? 'min-w-0 flex-1' : 'w-full max-w-none'}
		>
			<div
				className={
					placement === 'beside'
						? 'flex min-w-0 place-content-center place-items-center gap-3'
						: 'flex place-content-center place-items-center gap-3'
				}
			>
				{placement === 'beside' ? (
					// `truncate` needs to sit on the text-containing element itself, not the flex
					// container around it — a flex container's own overflow/white-space rules don't
					// apply ellipsis truncation to its (anonymous) flex-item children.
					<span className="truncate">
						<Translate value="corgi.petition_gamma.petition_sign.action" />
					</span>
				) : (
					<Translate value="corgi.petition_gamma.petition_sign.action" />
				)}
			</div>
		</Button>
	);

	return (
		<>
			{/* we're looking at breakpoints to decide whether to open the modal, which cannot be done server-side */}
			{/* Only auto-open on the non-sticky instance to avoid opening modal twice (both sticky and non-sticky render this component) */}
			{!sticky && (
				<ClientRender>
					<MobileSignButtonAutoOpen onOpen={openModalWithoutTracking} />
				</ClientRender>
			)}
			{/*
			 * Both instances of MobileActionButtonContainer render this component (its branch logic
			 * ignores `sticky`), and useTrackExperimentExposure dedups per mount, not across sibling
			 * mounts — an unguarded call here would fire $exposure twice per page view, halving every
			 * measured rate with no error and no SRM alarm. Same double-mount problem as
			 * MobileSignButtonAutoOpen above, same `!sticky` guard. Gated on `shareAction` (not on
			 * `sharePlacementVariation`) so control/fake_control fire the same $exposure as v1/v2 — the
			 * availability predicate is arm-independent by design. No device check needed here: the
			 * normalizer already forces `treated: false` for non-phone viewers, and
			 * useTrackExperimentExposure no-ops on `!treated`.
			 */}
			{!sticky && !!shareAction && (
				<ClientRender>
					<TrackExperimentExposure experiment={petitionDetailsShareCtaPlacementExperiment} />
				</ClientRender>
			)}
			{/* eslint-disable-next-line @change-corgi/require-data-qa-attribute */}
			<ActionButtonWrapper visible={visible} sticky={sticky}>
				{placement ? (
					<div className={placement === 'beside' ? 'flex items-center gap-2' : 'flex flex-col gap-2'}>
						{signButton}
						<SignCtaShareAffordance placement={placement} shareAction={shareAction} />
					</div>
				) : (
					signButton
				)}
			</ActionButtonWrapper>

			<MobileSignModal context={context} isOpen={isOpen} setIsOpen={setIsOpen} variant={variant} />
		</>
	);
};
function MobileSignButtonAutoOpen({ onOpen }: { onOpen: () => void }) {
	const showSignForm = useShowSignForm();

	useEffect(() => {
		if (showSignForm) {
			/**
			 * There are some updates that happen right before showing the modal (such as populating the form fields)
			 * that need to complete first so that the modal opens with the correct data.
			 * Using setTimeout to defer the onOpen call to the next event loop iteration and avoid a race condition.
			 */
			setTimeout(() => {
				onOpen();
			}, 0);
		}
		// disabling dependency check to avoid attempting to open the modal multiple times
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
}

function getSharePlacement(variation: PetitionDetailsShareCtaPlacementExperimentVariation): 'below' | 'beside' | null {
	if (variation === 'v1') return 'below';
	if (variation === 'v2') return 'beside';
	return null;
}
